/**
 * Boundary-splitting text renderer.
 *
 * Given a string and a set of possibly-overlapping ranges, cut the string at every
 * range boundary and emit either a plain string or a `<mark>` for each piece. That is
 * what lets several highlight layers coexist on one caption: the human tags, the
 * purple human-added-text layer, and (for admins) the agent's decision highlight.
 *
 * The output MUST be rendered into a single `white-space: pre-wrap` element with no
 * block-level children - see CaptionSurface and lib/offsets.js.
 */

/**
 * @param {string} text
 * @param {Array<{start:number,end:number,className?:string,title?:string,
 *                active?:boolean,id?:string,ariaLabel?:string,onSelect?:Function}>} ranges
 */
export function renderSegments(text, ranges) {
  const src = String(text ?? '');
  const clipped = (ranges || [])
    .filter(
      (r) =>
        r &&
        Number.isInteger(r.start) &&
        Number.isInteger(r.end) &&
        r.end > r.start &&
        r.start < src.length,
    )
    .map((r) => ({ ...r, start: Math.max(0, r.start), end: Math.min(src.length, r.end) }));

  if (clipped.length === 0) return [src];

  const cuts = new Set([0, src.length]);
  for (const r of clipped) {
    cuts.add(r.start);
    cuts.add(r.end);
  }
  const points = [...cuts].sort((a, b) => a - b);

  const out = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];
    if (to <= from) continue;
    const chunk = src.slice(from, to);

    const hits = clipped.filter((r) => from < r.end && to > r.start);
    if (hits.length === 0) {
      out.push(chunk);
      continue;
    }

    // Priority decides which range owns the tooltip and the click handler.
    const active = hits.find((r) => r.active);
    const clickable = hits.find((r) => r.onSelect);
    const passive = hits.find((r) => !r.onSelect && !r.active);
    const primary = active || clickable || passive || hits[0];

    // Classes layer in a fixed order so the active state paints last and wins. The
    // legacy stylesheet needed `!important` only because it concatenated blindly.
    const className =
      [clickable?.className, passive?.className, active?.className].filter(Boolean).join(' ') ||
      primary.className ||
      '';

    out.push(
      <mark
        key={`${from}-${to}-${className}`}
        className={className}
        title={primary.title}
        data-span-id={clickable?.id}
        data-active={active ? 'true' : undefined}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable?.ariaLabel ?? primary.title}
        // Prevents the click from collapsing the user's selection before we read it.
        onMouseDown={clickable ? (e) => e.stopPropagation() : undefined}
        onClick={
          clickable
            ? (e) => {
                e.stopPropagation();
                clickable.onSelect(e);
              }
            : undefined
        }
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  clickable.onSelect(e);
                }
              }
            : undefined
        }
      >
        {chunk}
      </mark>,
    );
  }
  return out;
}

/** Turn stored tag spans into renderable, clickable ranges. */
export function tagRanges(spans, { onSelect, activeIndex, meta }) {
  return (spans || []).map((span, index) => {
    const info = meta[span.label] || {};
    const note = String(span.notes || '').trim();
    const replacement = String(span.replacement_text || '').trim();
    const detail = note || replacement;
    return {
      start: span.start_char,
      end: span.end_char,
      className: `tagged-span ${info.className || ''}${index === activeIndex ? ' is-cursor' : ''}`,
      title: detail ? `${info.label}: ${detail}` : info.label,
      ariaLabel: `${info.label}${detail ? `, ${detail}` : ''}: "${span.text}"`,
      id: String(index),
      onSelect: onSelect ? (event) => onSelect(index, event) : undefined,
    };
  });
}
